// ============================================================
// 18. TaxReportPage.tsx
// ============================================================
// src/features/reports/pages/TaxReportPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getTaxReport, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    Receipt, DollarSign, TrendingUp, Calendar,
    FileText, BarChart3, PieChart, Percent
} from 'lucide-react';
import { motion } from 'framer-motion';

export function TaxReportPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [viewType, setViewType] = useState<'daily' | 'monthly'>('daily');

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-tax', tenantId, params],
        queryFn: () => getTaxReport(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString();

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'tax', format, params as any);
        window.open(url, '_blank');
    };

    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    const dailyData = data?.daily || [];
    const monthlyData = data?.monthly || [];
    const displayData = viewType === 'daily' ? dailyData : monthlyData;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
            <ReportHeader
                title="Tax Report"
                description="Tax collection analysis"
                icon={Receipt}
                iconGradient="from-blue-500 to-indigo-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data ? (
                    <EmptyState
                        icon={<Receipt className="w-10 h-10" />}
                        title="No Tax Data"
                        description="No tax data found for the selected period."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <SummaryCard
                                label="Total Tax"
                                value={formatNaira(data.summary?.totalTax || 0)}
                                icon={Receipt}
                                gradient="from-blue-500 to-indigo-600"
                            />
                            <SummaryCard
                                label="Total Revenue"
                                value={formatNaira(data.summary?.totalRevenue || 0)}
                                icon={DollarSign}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Effective Tax Rate"
                                value={`${data.summary?.effectiveTaxRate || 0}%`}
                                icon={Percent}
                                gradient="from-purple-500 to-violet-600"
                            />
                            <SummaryCard
                                label="Taxable Sales"
                                value={formatNumber(data.summary?.taxableSales || 0)}
                                icon={FileText}
                                gradient="from-amber-500 to-orange-600"
                            />
                            <SummaryCard
                                label="Tax Exempt"
                                value={formatNumber(data.summary?.taxExemptSales || 0)}
                                icon={PieChart}
                                gradient="from-slate-500 to-slate-600"
                            />
                        </div>

                        {/* View Toggle */}
                        <div className="flex gap-2">
                            <button
                                onClick={() => setViewType('daily')}
                                className={`px-4 py-2 text-sm font-medium rounded-lg ${viewType === 'daily' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-slate-500 border border-slate-200'
                                    }`}
                            >
                                Daily View
                            </button>
                            <button
                                onClick={() => setViewType('monthly')}
                                className={`px-4 py-2 text-sm font-medium rounded-lg ${viewType === 'monthly' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-slate-500 border border-slate-200'
                                    }`}
                            >
                                Monthly View
                            </button>
                        </div>

                        {/* Tax Trend Visualization */}
                        <Card>
                            <CardContent className="p-5">
                                <h3 className="text-sm font-semibold text-slate-800 mb-4">Tax Collection Trend</h3>
                                <div className="space-y-3">
                                    {displayData.slice(0, viewType === 'daily' ? 14 : 12).map((entry: any, i: number) => {
                                        const maxTax = Math.max(...displayData.map((d: any) => d.tax || 0), 1);
                                        const percentage = ((entry.tax || 0) / maxTax * 100);
                                        const dateLabel = viewType === 'daily'
                                            ? new Date(entry.date).toLocaleDateString('en-NG', { weekday: 'short', day: 'numeric', month: 'short' })
                                            : new Date(entry.month + '-01').toLocaleDateString('en-NG', { month: 'long', year: 'numeric' });

                                        return (
                                            <div key={i} className="flex items-center gap-3">
                                                <span className="text-xs text-slate-500 w-32">{dateLabel}</span>
                                                <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                                                    <motion.div
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${percentage}%` }}
                                                        transition={{ duration: 0.5, delay: i * 0.02 }}
                                                        className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-600"
                                                    />
                                                </div>
                                                <span className="text-xs font-bold text-slate-700 w-24 text-right">
                                                    {formatNaira(entry.tax)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">
                                                {viewType === 'daily' ? 'Date' : 'Month'}
                                            </th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Revenue</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Taxable Amount</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Tax Collected</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Tax Rate</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Cumulative Tax</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {displayData.map((entry: any, i: number) => {
                                            const dateLabel = viewType === 'daily'
                                                ? new Date(entry.date).toLocaleDateString('en-NG', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
                                                : new Date(entry.month + '-01').toLocaleDateString('en-NG', { month: 'long', year: 'numeric' });
                                            const taxRate = entry.revenue > 0 ? (entry.tax / entry.revenue * 100).toFixed(2) : '0';
                                            const cumulativeTax = displayData.slice(0, i + 1).reduce((s: number, d: any) => s + (d.tax || 0), 0);

                                            return (
                                                <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                                            <span className="text-sm font-medium text-slate-900">{dateLabel}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-slate-900">
                                                        {formatNaira(entry.revenue)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                        {formatNaira(entry.taxableAmount)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-blue-600">
                                                        {formatNaira(entry.tax)}
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <Badge variant="info" size="sm">{taxRate}%</Badge>
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                        {formatNaira(cumulativeTax)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}